import type {
  BrowserEventPayload,
  BrowserPixelAdapter,
  ScriptErrorReporter,
} from "../BrowserPixelAdapter";
import { injectVendorScript } from "../BrowserPixelAdapter";
import { buildTiktokUser, normalizeEmail, normalizeTiktokPhone } from "../AdvancedMatching";
import type { MatchIdentity } from "../AdvancedMatching";

export { TiktokPixelAdapter, TIKTOK_PIXEL_ADAPTER_KEY, TIKTOK_SCRIPT };

const TIKTOK_PIXEL_ADAPTER_KEY = "tiktok_pixel";
const TIKTOK_SCRIPT = "https://analytics.tiktok.com/i18n/pixel/events.js";
// Nombre del objeto global (`TiktokAnalyticsObject`) y `lib` del script: el
// mismo que usa el snippet oficial
const TIKTOK_LIB = "ttq";
// Nombre reservado del manifest: la página vista de TikTok es `page()`, no un
// `track`
const TIKTOK_PAGE_EVENT = "Pageview";
// Métodos que el snippet oficial encola, en su orden
const TIKTOK_METHODS: readonly string[] = [
  "page",
  "track",
  "identify",
  "instances",
  "debug",
  "on",
  "off",
  "once",
  "ready",
  "alias",
  "group",
  "enableCookie",
  "disableCookie",
  "holdConsent",
  "revokeConsent",
  "grantConsent",
];

// Lo que el SDK usa de una instancia de píxel (cola del stub o events.js)
interface TtqInstance {
  page: (...args: unknown[]) => void;
  track: (...args: unknown[]) => void;
  identify: (...args: unknown[]) => void;
}

// Lo que el SDK usa del objeto global (stub o events.js ya cargado)
interface TtqLike {
  instance: (pixelId: string) => TtqInstance;
  load?: (pixelId: string, options?: Record<string, unknown>) => void;
  grantConsent?: () => void;
  revokeConsent?: () => void;
  // pixel_id -> cola de su instancia; lo rellena `load`
  _i?: Record<string, unknown>;
}

// El stub del snippet oficial es un ARRAY (la cola global) con los métodos
// colgados como propiedades; cada instancia, otro array igual.
type DeferredQueue = unknown[] & Record<string, unknown>;

/**
 * Adaptador del píxel de TikTok (`ttq`).
 *
 * Particularidades de la red:
 *
 * - **Instancia compartida**: si `window.ttq` ya existe (el tenant instaló el
 *   snippet de TikTok, p.ej. por GTM) NO se redefine, igual que hace el
 *   snippet oficial; sus píxeles se dejan como están.
 * - **Un script por píxel**: el `ttq.load(id)` del snippet inyecta
 *   `events.js?sdkid=<id>&lib=ttq` para cada pixel_id. El stub de aquí hace lo
 *   mismo, pero la inyección pasa por `injectVendorScript` para que un bloqueo
 *   se reporte como en el resto de redes (`pixel_script_error`). Un píxel que
 *   ya cargó el tenant (`ttq._i[id]`) no se vuelve a cargar.
 * - **`ttq.instance(id)` siempre**: `ttq.track` a secas hace broadcast a todos
 *   los píxeles de la página, también a los ajenos, como `track` en Meta.
 * - **`Pageview` no es automático**: lo dispara el mapping `page_view` del
 *   manifest y sale como `instance(id).page()`, no como `track`.
 * - **Custom events**: la llamada no cambia (cualquier nombre no estándar es
 *   custom para TikTok), así que `event.custom` no se usa.
 * - **`event_id`** en las opciones de `track` es el gancho de la dedup con la
 *   Events API (mismo píxel + nombre + event_id).
 * - **Advanced matching pre-hasheado**: `instance(id).identify` con SHA-256,
 *   asíncrono como en OpenAI; el identify llega DETRÁS del Lead del propio
 *   optin (ver PixelManagerOptions.identity). Uno por identidad y píxel.
 */
class TiktokPixelAdapter implements BrowserPixelAdapter {
  readonly key = TIKTOK_PIXEL_ADAPTER_KEY;
  private _loaded = false;
  private _initialized: Record<string, boolean> = {};
  // Huella de la última identidad enviada por píxel: un identify por identidad
  private _identityApplied: Record<string, string> = {};
  private _onScriptError: ScriptErrorReporter | null;

  constructor(onScriptError?: ScriptErrorReporter | null) {
    this._onScriptError = onScriptError ?? null;
  }

  load(): void {
    if (this._loaded) {
      return;
    }
    this._loaded = true;
    try {
      if (this._ttq()) {
        // Ya cargado por el tenant o por GTM: instancia compartida.
        return;
      }

      window.TiktokAnalyticsObject = TIKTOK_LIB;
      window.ttq = createTtqStub((pixelId) => {
        injectVendorScript(
          tiktokScriptFor(pixelId),
          this.key,
          this._onScriptError,
        );
      });
    } catch {
      // sin píxel de TikTok la página sigue funcionando igual
    }
  }

  init(pixelId: string, identity: MatchIdentity | null): void {
    try {
      if (!pixelId) {
        return;
      }
      const ttq = this._ttq();
      if (!ttq) {
        return;
      }

      if (!this._initialized[pixelId]) {
        this._initialized[pixelId] = true;
        if (!isPixelLoaded(ttq, pixelId) && typeof ttq.load === "function") {
          ttq.load(pixelId);
        }
      }

      if (!identity) {
        return;
      }
      // Huella de lo que de verdad viaja a TikTok (normalizado, antes del
      // hash): un cambio de nombre no es una identidad nueva para él.
      const visitorId =
        typeof identity.visitorId === "string" ? identity.visitorId.trim() : "";
      const keys = [
        normalizeEmail(identity.email),
        normalizeTiktokPhone(identity.phone),
        visitorId,
      ];
      if (keys.every((key) => key === "")) {
        return;
      }
      const fingerprint = JSON.stringify(keys);
      if (this._identityApplied[pixelId] === fingerprint) {
        return;
      }
      this._identityApplied[pixelId] = fingerprint;

      void buildTiktokUser(identity)
        .then((user) => {
          try {
            if (Object.keys(user).length === 0) {
              return;
            }
            this._ttq()?.instance(pixelId).identify(user);
          } catch {
            // el identify es una mejora de matching, no un requisito: si
            // falla, los eventos siguen saliendo
          }
        })
        .catch(() => {
          // sin contexto seguro no hay hash: evento sin datos de usuario
        });
    } catch {
      // un init fallido no puede tumbar el resto de píxeles
    }
  }

  track(pixelId: string, event: BrowserEventPayload, eventId: string): void {
    try {
      const ttq = this._ttq();
      if (!ttq || !pixelId || !event?.name) {
        return;
      }
      const instance = ttq.instance(pixelId);
      if (event.name === TIKTOK_PAGE_EVENT) {
        instance.page();
        return;
      }
      instance.track(event.name, event.params ?? {}, { event_id: eventId });
    } catch {
      // el disparo de un píxel jamás afecta al lead ni a la navegación
    }
  }

  setConsent(granted: boolean): void {
    try {
      const ttq = this._ttq();
      if (granted) {
        ttq?.grantConsent?.();
      } else {
        ttq?.revokeConsent?.();
      }
    } catch {
      // sin consentimiento aplicado, el píxel se queda como estaba
    }
  }

  private _ttq(): TtqLike | null {
    const ttq = window.ttq;
    return ttq && typeof ttq.instance === "function" ? ttq : null;
  }
}

function tiktokScriptFor(pixelId: string): string {
  return (
    TIKTOK_SCRIPT + "?sdkid=" + encodeURIComponent(pixelId) + "&lib=" + TIKTOK_LIB
  );
}

function isPixelLoaded(ttq: TtqLike, pixelId: string): boolean {
  try {
    return (
      !!ttq._i &&
      typeof ttq._i === "object" &&
      Object.prototype.hasOwnProperty.call(ttq._i, pixelId)
    );
  } catch {
    return false;
  }
}

function setAndDefer(target: DeferredQueue, method: string): void {
  target[method] = (...args: unknown[]) => {
    target.push([method, ...args]);
  };
}

/**
 * Réplica del snippet oficial de TikTok (misma forma de cola, que es lo que
 * events.js drena): la cola global con `[método, ...args]`, `instance(id)`
 * sobre la cola del píxel en `_i[id]` y `load(id, opts)` registrando `_i`,
 * `_t` y `_o` como el original. Solo cambia cómo se inyecta el script: por
 * `injectScript` (el `injectVendorScript` del adaptador) en lugar del
 * `createElement` a pelo, para que su `onerror` llegue a la telemetría.
 */
function createTtqStub(injectScript: (pixelId: string) => void): DeferredQueue {
  const ttq = [] as unknown as DeferredQueue;
  ttq.methods = TIKTOK_METHODS.slice();
  ttq.setAndDefer = setAndDefer;
  for (const method of TIKTOK_METHODS) {
    setAndDefer(ttq, method);
  }
  ttq.instance = (pixelId: string) => {
    const registry = (ttq._i ?? {}) as Record<string, DeferredQueue>;
    const queue = registry[pixelId] ?? ([] as unknown as DeferredQueue);
    for (const method of TIKTOK_METHODS) {
      setAndDefer(queue, method);
    }
    return queue;
  };
  ttq.load = (pixelId: string, options?: Record<string, unknown>) => {
    const registry = (ttq._i ?? {}) as Record<string, DeferredQueue>;
    const queue = [] as unknown as DeferredQueue;
    queue._u = TIKTOK_SCRIPT;
    registry[pixelId] = queue;
    ttq._i = registry;
    const loadedAt = (ttq._t ?? {}) as Record<string, number>;
    loadedAt[pixelId] = Date.now();
    ttq._t = loadedAt;
    const loadOptions = (ttq._o ?? {}) as Record<string, unknown>;
    loadOptions[pixelId] = options ?? {};
    ttq._o = loadOptions;
    injectScript(pixelId);
  };
  return ttq;
}
